/**
 * **وجهةُ الرابط العميق — تطبيقٌ واحد، وبوابةٌ تمنع رابطاً بلا وجهة.**
 *
 * وهذا كان أوسعَ عطبٍ ظهوراً للمستخدم في المرحلة الرابعة: شاشةُ الإشعارات تعرف
 * **نمطاً واحداً** — `/tasks/{id}` — وتُعلَن في السجلّ أحدَ عشرَ شكلاً. فكل إشعار
 * عن كارت أو عميل أو شخص أو دور أو قسم أو طلب وصول أو تدقيق **يُسلَّم، ويُعرَض،
 * ويُوسَم مقروءاً — والنقرُ عليه يُعيد تحميل القائمة ولا يفتح شيئاً**.
 *
 * وبوابةُ «كل رابط فيه معرّف» كانت خضراء: تفحص **شكل النصّ**، لا أن أحداً
 * يستطيع فتحه. وتعليقُ الشاشة نفسها يقول «الرابط يفتح الصفّ نفسه» — **وعدٌ في
 * وثيقةٍ لا يقابله كود**، وهو ما تُبنى هذه المرحلة كلّها ضدّه.
 *
 * فمن هنا: **الأنماط معلَنةٌ هنا**، واختبارٌ يقابلها بسجلّ الإشعارات. فتعريفٌ
 * بشكلِ رابطٍ لا وجهةَ له **يكسر البناء**، ووجهةٌ لا يستعملها تعريفٌ تكسره كذلك
 * — فلا كودَ ميت.
 */

/** شاشاتُ القوقعة التي يصلها رابطٌ عميق */
export type DestinationScreen =
  /** **وشاشةُ البورتال واحدة هنا** — لوحةُ العنصر، وهي وجهةُ كل رابطٍ يصل العميل */
  | "portal-item"
  | "tasks"
  | "boards"
  | "clients"
  | "people"
  | "roles"
  | "departments"
  | "access"
  | "audit"
  /** **والشاتُ والاجتماعات** — المرحلة الثامنة.
   *
   * **وكانت وجهاتُهما الأربعُ مُعلَنةً بأسماءٍ ليست في هذا النوع**: فالملفُّ
   * لا يُبنى أصلاً، **وبوابةُ الوجهات خضراءُ** — لأنها تقرأ الخريطةَ قيماً
   * وقتَ التشغيل، **والنوعُ يُمحى هناك**. فمن يحرس الأسماءَ هو المترجِم وحده.
   */
  | "chat"
  | "meetings"
  /** **وشاشتا البورتال الأخريان** — خيطُه ومواعيدُه */
  | "portal-chat"
  | "portal-meetings"
  /** **وسجلُّ التغييرات** — المرحلة الحادية عشرة */
  | "changelog"
  | "storage"
  | "plans"
  /** **وطابورُ النشر** — المرحلة السابعة */
  | "moderation"
  /** **ولوحةُ التحليلات** — المرحلة العاشرة */
  | "analytics"
  /** **والتقارير** — المرحلة التاسعة */
  | "reports"
  | "settings"
  /** **و«حسابي»** — وجهةُ تنبيه التجربة */
  | "profile";

/**
 * **نوعُ الصفّ المقصود** — والشاشةُ وحدها لا تكفي.
 *
 * فالكارتُ والبوردُ يسكنان شاشةً واحدة، ومعرّفُ كارتٍ يُفتَح كأنه بورد يُعطي
 * «غير موجود». وكتبتُ الوجهةَ أوّلاً بالشاشة وحدها **فوقع ذلك بالنقر**: رابطُ
 * تذكير الموعد يفتح شاشةَ خطأ بكودها. وكان الخطأ صريحاً بكوده ورقم ربطه —
 * **وذلك ما يجعله يُكتشَف في دقيقة** بدل أن يسكن سنتين.
 */
export type DestinationEntity =
  | "task"
  /** **والخطةُ نفسُها** — فإشعارُ النشر يفتحها لا عنصراً معلّقاً في فراغ */
  | "plan"
  /** **وطلبُ الإزالة** — صفٌّ في الطابور، لا الطابور */
  | "takedown"
  /** **والتقريرُ وطلبُ استثنائه** — صفّان على شاشةٍ واحدة، ولا يُفتَح أحدهما مكان الآخر */
  | "report"
  | "report_exception"
  /** **وهدفُ الشركة** — صفٌّ في لوحة التحليلات */
  | "company_goal"
  /** عنصرُ خطةٍ **كما يفتحه العميل** — لوحةٌ بورتالية لا شاشةُ الخطط الداخلية */
  | "portal_item"
  | "card"
  | "plan_item"
  | "plan_task"
  | "board"
  | "client"
  | "person"
  | "role"
  | "department"
  | "access_request"
  | "audit_client"
  /** **ورسالةُ الشات والاجتماع** — المرحلة الثامنة */
  | "chat_message"
  | "meeting"
  | "portal_message"
  | "portal_meeting"
  /** **والإصدارُ المنشور** — يفتحه كلُّ من وصله الخبر */
  | "release"
  | "orphan"
  | "screen";

/**
 * **أيُّ قوقعةٍ تفتح هذه الوجهة.**
 *
 * فجهةُ الاتصال لا تملك `/plan-items/:id` الداخلي، ولو حمل إشعارُها ذلك الرابط
 * لانتهى بها إلى رفض — **إشعارٌ يصل ويُنقَر ويُفشِل**. والقوقعةُ تُختار بنوع
 * الحساب، فالوجهةُ تُعلن لأيّهما هي، **وكلُّ قوقعةٍ تستنفد وجهاتها بالمترجِم**.
 */
export type DestinationShell = "staff" | "portal";

export type Destination = {
  readonly shell: DestinationShell;
  readonly screen: DestinationScreen;
  readonly entity: DestinationEntity;
  /**
   * الصفُّ المقصود — **وتفتحه الشاشة**، لا تكتفي بعرض قائمتها.
   *
   * ويكون فارغاً لشاشةٍ واحدة لا صفَّ لها: إعدادات النظام.
   */
  readonly focusId: string | null;
  /**
   * لوحٌ داخلي، إن كان الرابط يقصده — «فريق العميل» لا بيانات العميل.
   */
  readonly tab?: string;
};

/**
 * الأنماطُ المعلَنة — **بنصّها كما في سجلّ الإشعارات**، فتُقابَل حرفاً بحرف.
 *
 * ولا تُقابَل بتعبيرٍ نمطي يُبنى تخميناً: كتبتُ واحداً كذلك في بوابةٍ أخرى
 * **فأعطى تحذيرين كاذبين**، والتحذير الكاذب أسوأ من لا تحذير.
 */
export const LINK_DESTINATIONS: Readonly<Record<string, Destination>> = {
  "/tasks/{id}": { shell: "staff", screen: "tasks", entity: "task", focusId: "{id}" },
  /** **وعنصرُ الخطة يُفتَح في خطته** — كالكارت في بورده */
  "/plan-items/{id}": { shell: "staff", screen: "plans", entity: "plan_item", focusId: "{id}" },
  /**
   * **ومهمةُ العنصر تُفتَح على عنصرها** — فإشعارُ «مهمتك رجعت» يفتح لوحتَه.
   * ويُسأل عن عنصرها أوّلاً، كالكارت يُسأل عن بورده.
   */
  "/plan-tasks/{id}": { shell: "staff", screen: "plans", entity: "plan_task", focusId: "{id}" },
  /**
   * **وإشعارُ النشر يفتح خطّتَه** — فالعنصرُ يُقرأ في سياقه لا وحده.
   *
   * ومن يُبلَّغ أن بوستَه نزل يريد أن يرى **أين هو من خطّة الشهر**، لا صفّاً
   * معلّقاً في فراغ.
   */
  "/plans/{id}": { shell: "staff", screen: "plans", entity: "plan", focusId: "{id}" },
  /**
   * **وطلبُ الإزالة يفتح صفَّه في الطابور** — لا الطابورَ كلَّه.
   *
   * «كل رابط عميق يصل إلى صفّ، لا إلى قائمة يبحث فيها صاحبه». ومن يُبلَّغ
   * بطلبٍ عاجل آخرُ ما يحتاجه أن يدوّر في مئة صفّ.
   */
  "/moderation/takedowns/{id}": {
    shell: "staff",
    screen: "moderation",
    entity: "takedown",
    focusId: "{id}",
  },
  /**
   * **والهدفُ يُفتَح على صفّه** — فمن يُبلَّغ بكسرِ هدفٍ يريد ذلك الهدفَ بعينه.
   *
   * ولا قائمةً يبحث فيها: الأهدافُ قد تكون عشرين صفّاً على شهورٍ مختلفة،
   * **وبلا تعليمِ صفّه يُقرأ الإشعارُ ويُنسى**.
   */
  "/analytics/goals/{id}": {
    shell: "staff",
    screen: "analytics",
    entity: "company_goal",
    focusId: "{id}",
  },
  /**
   * **والتقريرُ يُفتَح على صفّه** — لا على قائمةٍ يبحث فيها صاحبُه.
   *
   * ومن يُبلَّغ أن تقريراً ينتظر مراجعتَه، أو أن تقريرَه رجع بسبب، **يريد
   * الصفَّ نفسَه**. وكانت التعريفاتُ الخمسة تُعلن هذين الشكلين **ولا شاشةَ
   * تفتحهما**: يُسلَّم الإشعارُ، ويُعرَض، ويُوسَم مقروءاً، والنقرُ يُعيد تحميل
   * القائمة. وأمسكته البوّابةُ نفسُها التي بُنيت لهذا بعد المرحلة الرابعة.
   */
  "/reports/{id}": { shell: "staff", screen: "reports", entity: "report", focusId: "{id}" },
  "/report-exceptions/{id}": {
    shell: "staff",
    screen: "reports",
    entity: "report_exception",
    focusId: "{id}",
  },
  /** **والكارتُ يُفتَح في بورده** — فيُسأل عن بورده أوّلاً، ولا يُفتَح كأنه بورد */
  "/cards/{id}": { shell: "staff", screen: "boards", entity: "card", focusId: "{id}" },
  "/boards/{id}": { shell: "staff", screen: "boards", entity: "board", focusId: "{id}" },
  "/clients/{id}": { shell: "staff", screen: "clients", entity: "client", focusId: "{id}" },
  "/clients/{id}/team": {
    shell: "staff",
    screen: "clients",
    entity: "client",
    focusId: "{id}",
    tab: "team",
  },
  "/people/{id}": { shell: "staff", screen: "people", entity: "person", focusId: "{id}" },
  "/roles/{id}": { shell: "staff", screen: "roles", entity: "role", focusId: "{id}" },
  "/departments/{id}": {
    shell: "staff",
    screen: "departments",
    entity: "department",
    focusId: "{id}",
  },
  "/access-requests/{id}": {
    shell: "staff",
    screen: "access",
    entity: "access_request",
    focusId: "{id}",
  },
  "/audit/clients/{id}": {
    shell: "staff",
    screen: "audit",
    entity: "audit_client",
    focusId: "{id}",
  },
  "/storage/orphans/{id}": { shell: "staff", screen: "storage", entity: "orphan", focusId: "{id}" },
  /**
   * **وإصدارٌ منشورٌ يُفتَح على نصّه** — لا على قائمةِ نسخٍ يبحث فيها صاحبُه.
   *
   * **وجمهورُه كلُّ من يكتب في الشات** — أي كلُّ موظّف — **وقراءةُ المنشور
   * بمعرّفه مفتوحةٌ لكل من دخل**: فلو حُرِست بقدرة الإدارة لوصل الخبرُ ونُقِر
   * فرُفِض، وهو الصنفُ الذي بُنيت هذه الخريطةُ كلُّها ضدّه.
   */
  "/releases/{id}": { shell: "staff", screen: "changelog", entity: "release", focusId: "{id}" },
  /**
   * **ورسالةُ الشات تُفتَح في قناتها** — فالكلامُ يُقرأ في خيطه.
   *
   * والشاشةُ تسأل السيرفرَ عن قناة الرسالة ثم تفتحها **وتُعلِّم صفَّها**:
   * رسالةٌ وحدها معلّقةٌ في فراغ لا تُفهَم، **وقائمةُ قنواتٍ تجعل صاحبَ
   * الإشعار يدوّر**.
   */
  "/chat/{id}": { shell: "staff", screen: "chat", entity: "chat_message", focusId: "{id}" },
  /**
   * **واجتماعٌ يُفتَح على لوحته** — فمن أُبلِغ بطلبٍ يحتاج أن يردّ عليه.
   *
   * ولا قائمةُ اجتماعاتٍ يبحث فيها: **«كلُّ رابطٍ عميق يصل إلى صفّ»** — ومن
   * يُبلَّغ بإلغاءِ موعدٍ آخرُ ما يحتاجه أن يدوّر بين عشرين موعداً.
   */
  "/meetings/{id}": { shell: "staff", screen: "meetings", entity: "meeting", focusId: "{id}" },
  /** الشاشةُ الوحيدة بلا صفّ — واستثناءٌ معلَنٌ في البوابة كذلك */
  "/settings": { shell: "staff", screen: "settings", entity: "screen", focusId: null },
  /** **وتنبيهُ التجربة يعود إلى زرّه** — «حسابي» حيث ضُغط، لا صفَّ له */
  "/profile": { shell: "staff", screen: "profile", entity: "screen", focusId: null },
  /**
   * **ورابطُ العميل بورتاليّ** — فيفتح لوحتَه هو، لا شاشةَ الخطط الداخلية.
   *
   * وبلا هذا السطر يصل الإشعارُ ويُنقَر **فيُرفَض**: جهةُ الاتصال لا تحمل
   * `plans.view`. فالقوقعةُ تُختار بنوع الحساب، والوجهةُ تُعلن لأيّهما هي.
   */
  "/portal/items/{id}": {
    shell: "portal",
    screen: "portal-item",
    entity: "portal_item",
    focusId: "{id}",
  },
  /**
   * **وردُّ الوكالة يفتح خيطَه** — لا لوحةَ عنصرٍ بمعرّفِ رسالة.
   *
   * **وبلا هذا السطر كانت القوقعةُ البورتالية تفتح أيَّ وجهةٍ بورتالية لوحةَ
   * عنصر**: فرابطُ ردٍّ يصل بمعرّف رسالة، فتُفتَح لوحةُ عنصرٍ لا وجود له —
   * «العنصر مش موجود» في وجه من جاء يقرأ ردّاً وصله.
   */
  "/portal/chat/{id}": {
    shell: "portal",
    screen: "portal-chat",
    entity: "portal_message",
    focusId: "{id}",
  },
  /** **واجتماعُه يفتح شاشتَه** — فالموعدُ يُقرأ مع بقيّة مواعيده */
  "/portal/meetings/{id}": {
    shell: "portal",
    screen: "portal-meetings",
    entity: "portal_meeting",
    focusId: "{id}",
  },
};

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * يقرأ رابطاً فعلياً — **بالأنماط المعلَنة، لا بوصله بالمتصفّح**.
 *
 * فقيمةٌ من القاعدة لا تصير وجهةً بلا فحص: المعرّف يُطابَق شكلاً، وما لا نمطَ
 * له **يُعاد فارغاً** فتقول الشاشة ذلك بدل أن تصمت.
 */
export function destinationOf(link: string): Destination | null {
  const direct = LINK_DESTINATIONS[link];
  if (direct && direct.focusId === null) return direct;

  for (const [pattern, destination] of Object.entries(LINK_DESTINATIONS)) {
    if (destination.focusId === null) continue;
    const at = pattern.indexOf("{id}");
    if (at === -1) continue;
    const head = pattern.slice(0, at);
    const tail = pattern.slice(at + "{id}".length);
    if (!link.startsWith(head) || !link.endsWith(tail)) continue;
    const id = link.slice(head.length, link.length - tail.length);
    if (!ID.test(id)) continue;
    return { ...destination, focusId: id };
  }
  return null;
}
